import React, {useEffect} from 'react';
import {Check, X} from 'lucide-react';
import {YourCartProps} from "../../@types/cart";

const YourCart: React.FC<YourCartProps> = ({ cart, onSignup, onChangeAddress, buttonText, isLoading, hideButton, onValidated }) => {

  const payMonthly = cart?.products?.filter((product) => product.isMonthly)?.sort((a, b) => {
    if (a.type === 'PLAN' && b.type !== 'PLAN') return -1;
    if (a.type !== 'PLAN' && b.type === 'PLAN') return 1;
    return 0;
  });
  const payToday = cart?.products?.filter((product) => !product.isMonthly && (product.cost > 0 || product.type === 'PROMO'))?.sort((a, b) => {
    if (a.type === 'ROUTER' && b.type === 'SHIPPING') return -1;
    if (a.type === 'SHIPPING' && b.type === 'ROUTER') return 1;
    return 0;
  });

  const hasPlan = cart?.products?.find((product) => product.type === 'PLAN') !== undefined;
  const hasRouter = cart?.products?.find((product) => product.type === 'ROUTER') !== undefined;
  const termedItem = cart?.products?.find((product) => product.type === 'ROUTER' && product.months > 0);

  // Separate promo items from regular products
  const promoItems = cart?.products?.filter(product => product.type === 'PROMO') || [];
  const regularMonthlyItems = payMonthly?.filter(product => product.type !== 'PROMO') || [];
  const regularOneOffItems = payToday?.filter(product => product.type !== 'PROMO') || [];
  
  // Calculate totals (promo items have negative costs, so they naturally reduce totals)
  const monthlyTotal = regularMonthlyItems?.reduce((sum, product) => sum + (product.cost / 100), 0) || 0;
  const oneOffTotal = regularOneOffItems?.reduce((sum, product) => sum + (product.cost / 100), 0) || 0;
  
  const monthlyDiscount = Math.abs(promoItems?.filter(p => p.isMonthly).reduce((sum, product) => sum + (product.cost / 100), 0) || 0);
  const oneOffDiscount = Math.abs(promoItems?.filter(p => !p.isMonthly).reduce((sum, product) => sum + (product.cost / 100), 0) || 0);
  
  // Final totals include promo discounts naturally
  const discountedMonthlyTotal = Math.max(0, payMonthly?.reduce((sum, product) => sum + (product.cost / 100), 0) || 0);
  const discountedOneOffTotal = Math.max(0, payToday?.reduce((sum, product) => sum + (product.cost / 100), 0) || 0);

  useEffect(() => {
    if (onValidated) {
      onValidated(hasPlan && hasRouter);
    }
  }, [hasPlan, hasRouter, onValidated]);

  return (
      <div className='lg:col-span-1 hidden lg:flex'>
        <div className="w-full shadow-md bg-white border border-gray-200 rounded-lg">
          <div className='mb-2 bg-fred-blue-500 rounded-t-lg'>
            <h3 className="text-2xl text-center px-8 py-4 font-medium text-white">Your Cart</h3>
          </div>
          <div className='p-4 space-y-4'>
            <div className=''>
              <h5 className='mb-1 text-xl font-medium'>Your Address</h5>
              <p className='text-base'>{cart?.address?.fullAddress}</p>
              <p>
                <span className="font-bold underline cursor-pointer" onClick={() => onChangeAddress()}>CHANGE</span>
              </p>
            </div>
            <div>
              <h5 className='text-xl font-medium'>Products</h5>
              <div className="flex flex-col w-full space-y-1 mt-2">
                {!cart?.products && (
                    <p className='text-base font-normal'>No products selected</p>
                )}
                {cart?.products && cart.products.filter((product) => product.type === 'PLAN').map((product) => {
                  return (
                      <p key={product.productId} className='text-base'>{`${product.productName}${termedItem ? ` (${termedItem.months}-month term)` : ''}`}</p>
                  )
                })}
                {cart?.products && cart.products.filter((product) => product.type === 'ROUTER').map((product) => {
                  return (
                      <p key={product.productId} className='text-base'>{product.productName}</p>
                  )
                })}
              </div>
            </div>
            {(payMonthly && payMonthly.length > 0) && (
                <div>
                  <h5 className='text-xl font-medium mb-2'>Monthly Payment</h5>
                  {payMonthly.map((product) => {
                    return (
                        <div key={product.productId}>
                          <div className='flex items-center justify-between space-y-1'>
                            <p className={`text-base ${product.type === 'PROMO' ? 'text-green-600 font-semibold' : ''}`}>{product.productName}</p>
                            <p className={`text-base ${product.type === 'PROMO' ? 'text-green-600 font-semibold' : ''}`}>{product.type === 'PROMO' ? `-$${Math.abs(product.cost / 100)}` : `$${product.cost / 100}`}</p>
                          </div>
                        </div>
                    )
                  })}
                  <div className='flex justify-between items-center mt-2'>
                    <p className='text-base font-normal text-fred-purple-500'>Monthly Total</p>
                    {monthlyDiscount > 0 ? (
                      <p className='text-3xl font-semibold text-green-600'>${discountedMonthlyTotal}</p>
                    ) : (
                      <p className='text-3xl font-semibold text-fred-purple-500'>${discountedMonthlyTotal}</p>
                    )}
                  </div>
                </div>
            )}
            {(payToday && payToday.length > 0) && (
                <div>
                  <h5 className='text-xl font-medium mb-2'>One-off Payment</h5>
                  {payToday.map((product) => {
                    return (
                        <div key={product.productId} className='flex items-center justify-between space-y-1'>
                          <p className={`text-base ${product.type === 'PROMO' ? 'text-green-600 font-semibold' : ''}`}>{product.productName}</p>
                          <p className={`text-base ${product.type === 'PROMO' ? 'text-green-600 font-semibold' : ''}`}>{product.type === 'PROMO' ? `-$${Math.abs(product.cost / 100)}` : `$${product.cost / 100}`}</p>
                        </div>
                    )
                  })}
                  <div className='flex justify-between items-center mt-2'>
                    <p className='text-base font-normal text-fred-purple-500'>One-off Total</p>
                    {oneOffDiscount > 0 ? (
                      <p className='text-3xl font-semibold text-green-600'>${discountedOneOffTotal}</p>
                    ) : (
                      <p className='text-3xl font-semibold text-fred-purple-500'>${discountedOneOffTotal}</p>
                    )}
                  </div>
                </div>
            )}
          </div>
          <div className='px-4'>
            <p className="text-sm italic">All prices are inclusive of GST</p>
          </div>
          <div className='p-4 flex justify-between gap-x-4'>
            <div
                className={`w-1/2 flex items-center p-2 text-sm rounded-lg ${hasPlan ? 'text-green-800 bg-green-50' : 'text-red-800 bg-red-50'}`}
                role="alert">
              {hasPlan ? (
                <Check className="flex-shrink-0 inline w-5 h-5 me-2" />
              ) : (
                <X className="flex-shrink-0 inline w-5 h-5 me-2" />
              )}
              <div>
                <span className="font-medium">Base plan</span>
              </div>
            </div>
            <div className={`w-1/2 flex items-center p-2 text-sm rounded-lg ${hasRouter ? 'text-green-800 bg-green-50' : 'text-red-800 bg-red-50'}`} role="alert">
              {hasRouter ? (
                <Check className="flex-shrink-0 inline w-5 h-5 me-2" />
              ) : (
                <X className="flex-shrink-0 inline w-5 h-5 me-2" />
              )}
              <div>
                <span className="font-medium">Router</span>
              </div>
            </div>
          </div>
          {!hideButton && (
            <div className='p-4 flex justify-center'>
              <button disabled={!hasPlan || !hasRouter || isLoading} type="button"
                      onClick={() => onSignup?.()}
                      className="w-1/2 text-white bg-fred-purple-500 hover:bg-fred-blue-500 font-semibold rounded-lg text-base px-4 py-4 text-center inline-flex items-center justify-center cursor-pointer disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400">
                {/*<svg aria-hidden="true" role="status" className="inline w-6 h-6 me-4 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"/>
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
                  </svg>*/}
                {buttonText ? buttonText : 'Sign Up Now'}
              </button>
            </div>
          )}
        </div>
      </div>
  );
};

export default YourCart;
